import React from "react";
import { Trash2, Plus, GripVertical } from "lucide-react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import {
  ZONE_OPTIONS,
  UNIT_OPTIONS,
  SEGMENT_LABEL_OPTIONS,
  zonesToRpe,
  mkWork,
  mkRest,
  mkSimple,
} from "../../lib/coaching";
import { suggestedPaceFromZones, suggestedHrFromZones } from "../../lib/vdot";

/**
 * A single simple segment row (Warm-up / Work / Rest / Cool-down / ...).
 * Props:
 *   seg: the segment object
 *   onChange: (updated) => void
 *   onDelete: () => void (optional — if provided, shows delete btn)
 *   compact: boolean — hides label field when shown inside a group
 *   athletePaces: optional training paces (from VDOT) for pace auto-fill
 */
function SimpleSegmentRow({ seg, onChange, onDelete, compact = false, athletePaces, dragHandleProps }) {
  const update = (patch) => onChange({ ...seg, ...patch });

  const suggestedPace = suggestedPaceFromZones(seg, athletePaces);
  const suggestedHr = suggestedHrFromZones(seg, athletePaces);

  const toggleZone = (z) => {
    const set = new Set(seg.zones || []);
    if (set.has(z)) set.delete(z);
    else set.add(z);
    const zones = Array.from(set).sort();
    const [rmin, rmax] = zonesToRpe(zones);
    // Auto-fill pace with the freshly-suggested range whenever the coach
    // touches a zone. Two cases qualify for overwrite (so we never stomp
    // a custom pace the coach typed):
    //   1. pace is empty → nothing to lose
    //   2. pace exactly equals the suggested pace BEFORE this toggle (i.e.
    //      it was auto-filled previously) → safe to refresh
    // Anything else (a custom string like "5:30 chip") is preserved.
    const newSuggested = suggestedPaceFromZones({ ...seg, zones }, athletePaces);
    const wasAutoFilled = !seg.pace || seg.pace === suggestedPace;
    const patch = { zones, rpe_min: rmin, rpe_max: rmax };
    if (wasAutoFilled && newSuggested) {
      patch.pace = newSuggested;
    }
    update(patch);
  };

  // Auto-refresh the saved pace string when the athlete's underlying paces
  // change (e.g. coach updated the lactate test). We only overwrite when
  // the saved pace looks like a previous auto-fill — i.e. it matches a
  // "M:SS-M:SS/km" range AND differs from the current suggestion. Custom
  // notes ("5:30 chip", "feels comfortable") never match the regex and
  // stay untouched.
  React.useEffect(() => {
    if (!suggestedPace) return;
    const saved = (seg.pace || "").trim();
    if (!saved) return;
    if (saved === suggestedPace) return;
    // Recognise three auto-fill shapes so the saved value refreshes when
    // the athlete's test changes: closed range ("5:21-6:01/km"), open-end
    // slower ("6:27+/km"), and open-end faster ("<4:58/km").
    const looksAutoFilled =
      /^\s*\d+:\d{2}\s*[-–—]\s*\d+:\d{2}\s*\/?\s*km\s*$/i.test(saved) ||
      /^\s*\d+:\d{2}\s*\+\s*\/?\s*km\s*$/i.test(saved) ||
      /^\s*<\s*\d+:\d{2}\s*\/?\s*km\s*$/i.test(saved);
    if (looksAutoFilled) {
      update({ pace: suggestedPace });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedPace]);

  return (
    <div className="border border-[var(--tir-border)] bg-white p-3 space-y-2.5">
      <div className="flex items-start gap-2">
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] cursor-grab active:cursor-grabbing mt-0.5 -ml-1"
            aria-label="Drag to reorder"
            title="Drag to reorder"
            data-testid={`segment-drag-handle-${seg.id}`}
          >
            <GripVertical size={14} />
          </span>
        )}
        {!compact && (
          <select
            value={SEGMENT_LABEL_OPTIONS.includes(seg.label) ? seg.label : "Custom"}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "Custom") update({ label: seg.label || "" });
              else update({ label: v });
            }}
            className="bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 text-xs tracking-[0.15em] uppercase font-semibold text-[var(--tir-ink)] focus:outline-none min-w-[110px]"
          >
            {SEGMENT_LABEL_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        )}
        {compact && (
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)]">
            {seg.label || "Segment"}
          </div>
        )}
        {!SEGMENT_LABEL_OPTIONS.includes(seg.label) && !compact && (
          <input
            value={seg.label}
            onChange={(e) => update({ label: e.target.value })}
            placeholder="Custom label"
            className="bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 text-xs focus:outline-none flex-1"
          />
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="ml-auto text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] p-1"
            aria-label="Delete segment"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <input
          value={seg.value || ""}
          onChange={(e) => update({ value: e.target.value })}
          placeholder="10"
          inputMode="decimal"
          data-testid="segment-value-input"
          className="w-16 bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-1 py-1 font-mono-tir focus:outline-none"
        />
        <select
          value={seg.unit || "min"}
          onChange={(e) => update({ unit: e.target.value })}
          className="bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm focus:outline-none"
        >
          {UNIT_OPTIONS.map((u) => (
            <option key={u.value} value={u.value}>
              {u.label}
            </option>
          ))}
        </select>
        <span className="text-[var(--tir-muted)] mx-1">@</span>
        <div className="flex gap-1">
          {ZONE_OPTIONS.map((z) => {
            const active = (seg.zones || []).includes(z);
            return (
              <button
                key={z}
                type="button"
                onClick={() => toggleZone(z)}
                className={`h-7 w-7 border text-[11px] font-semibold font-mono-tir transition-colors ${
                  active
                    ? "bg-[var(--tir-burgundy)] text-white border-[var(--tir-burgundy)]"
                    : "bg-white text-[var(--tir-ink)] border-[var(--tir-border)] hover:border-[var(--tir-ink)]"
                }`}
              >
                Z{z}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1 ml-2">
          <span className="text-[10px] tracking-[0.15em] uppercase text-[var(--tir-muted)]">
            RPE
          </span>
          <input
            type="number"
            min="1"
            max="10"
            value={seg.rpe_min ?? ""}
            onChange={(e) =>
              update({ rpe_min: e.target.value === "" ? null : Number(e.target.value) })
            }
            className="w-12 bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm text-center font-mono-tir focus:outline-none"
          />
          <span className="text-[var(--tir-muted)]">-</span>
          <input
            type="number"
            min="1"
            max="10"
            value={seg.rpe_max ?? ""}
            onChange={(e) =>
              update({ rpe_max: e.target.value === "" ? null : Number(e.target.value) })
            }
            className="w-12 bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-sm text-center font-mono-tir focus:outline-none"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <label className="flex items-center gap-1.5">
          <span className="text-[10px] tracking-[0.15em] uppercase text-[var(--tir-muted)] font-semibold">
            Pace
          </span>
          <input
            value={seg.pace || ""}
            onChange={(e) => update({ pace: e.target.value })}
            placeholder={suggestedPace || "5:30/km"}
            data-testid="segment-pace-input"
            className="w-32 bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-1 py-0.5 font-mono-tir focus:outline-none"
          />
          {suggestedPace && seg.pace !== suggestedPace && (
            <button
              type="button"
              onClick={() => update({ pace: suggestedPace })}
              data-testid="use-suggested-pace-btn"
              className="text-[9px] tracking-[0.16em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] px-1 py-0.5 border border-dashed border-[var(--tir-burgundy)]/40 hover:border-[var(--tir-burgundy)] whitespace-nowrap"
              title="Use suggested pace from VDOT profile"
            >
              use {suggestedPace}
            </button>
          )}
          {suggestedHr && (
            <span
              className="text-[10px] font-mono-tir text-[var(--tir-muted)] whitespace-nowrap"
              data-testid="segment-suggested-hr"
              title="Heart-rate bracket from this athlete's most recent lactate test"
            >
              · {suggestedHr}
            </span>
          )}
        </label>
        <label className="flex items-center gap-1.5">
          <span className="text-[10px] tracking-[0.15em] uppercase text-[var(--tir-muted)] font-semibold">
            Incline
          </span>
          <input
            value={seg.incline || ""}
            onChange={(e) => update({ incline: e.target.value })}
            placeholder="2"
            className="w-12 bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-1 py-0.5 font-mono-tir focus:outline-none text-center"
          />
          <span className="text-[10px] text-[var(--tir-muted)]">%</span>
        </label>
        <input
          value={seg.notes || ""}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="Notes (e.g. add 4 strides)"
          className="flex-1 min-w-[140px] bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-1 py-0.5 text-xs focus:outline-none"
        />
      </div>
    </div>
  );
}

/**
 * A repeat-group segment (e.g. "Main Set · repeat 6×" containing Work/Rest items).
 */
function GroupSegmentRow({ seg, onChange, onDelete, athletePaces, dragHandleProps }) {
  const update = (patch) => onChange({ ...seg, ...patch });

  const updateItem = (idx, updated) => {
    const items = seg.items.map((it, i) => (i === idx ? updated : it));
    update({ items });
  };

  const deleteItem = (idx) =>
    update({ items: seg.items.filter((_, i) => i !== idx) });

  const addItem = (kind) => {
    const factory = kind === "rest" ? mkRest : kind === "work" ? mkWork : mkSimple;
    update({ items: [...seg.items, factory()] });
  };

  return (
    <div className="border-2 border-[var(--tir-ink)] bg-[var(--tir-ivory)] p-3">
      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[var(--tir-border)]">
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] cursor-grab active:cursor-grabbing -ml-1"
            aria-label="Drag to reorder section"
            title="Drag to reorder section"
            data-testid={`segment-drag-handle-${seg.id}`}
          >
            <GripVertical size={16} />
          </span>
        )}
        <input
          value={seg.label || ""}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Main Set"
          className="font-serif-display text-lg text-[var(--tir-ink)] bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-0.5 focus:outline-none flex-1"
        />
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--tir-muted)] font-semibold">
            Repeat
          </span>
          <input
            type="number"
            min="1"
            max="50"
            value={seg.repeat_count ?? 1}
            onChange={(e) =>
              update({ repeat_count: e.target.value === "" ? 1 : Number(e.target.value) })
            }
            className="w-14 bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2 py-1 text-center text-sm font-mono-tir focus:outline-none"
          />
          <span className="text-sm font-semibold text-[var(--tir-ink)]">×</span>
        </div>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] p-1"
            aria-label="Delete group"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <Droppable droppableId={`group-${seg.id}`} type="group-item">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2" data-testid={`group-items-${seg.id}`}>
            {(seg.items || []).map((it, idx) => (
              <Draggable key={it.id || `i${idx}`} draggableId={`item-${it.id || `${seg.id}-${idx}`}`} index={idx}>
                {(dp, snapshot) => (
                  <div ref={dp.innerRef} {...dp.draggableProps} className={snapshot.isDragging ? "shadow-lg ring-2 ring-[var(--tir-burgundy)]/40" : ""}>
                    <SimpleSegmentRow
                      seg={it}
                      compact
                      onChange={(u) => updateItem(idx, u)}
                      onDelete={() => deleteItem(idx)}
                      athletePaces={athletePaces}
                      dragHandleProps={dp.dragHandleProps}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--tir-border)]">
        <button
          type="button"
          onClick={() => addItem("work")}
          className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
        >
          <Plus size={12} /> Work
        </button>
        <button
          type="button"
          onClick={() => addItem("rest")}
          className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
        >
          <Plus size={12} /> Rest
        </button>
        <button
          type="button"
          onClick={() => addItem("custom")}
          className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
        >
          <Plus size={12} /> Custom
        </button>
      </div>
    </div>
  );
}

/**
 * One segment row — dispatches simple vs group.
 */
export function SegmentRow({ seg, onChange, onDelete, athletePaces, dragHandleProps }) {
  if (seg.kind === "group") {
    return <GroupSegmentRow seg={seg} onChange={onChange} onDelete={onDelete} athletePaces={athletePaces} dragHandleProps={dragHandleProps} />;
  }
  return <SimpleSegmentRow seg={seg} onChange={onChange} onDelete={onDelete} athletePaces={athletePaces} dragHandleProps={dragHandleProps} />;
}

/** Move an item within a list (immutable). */
export function reorderList(list, from, to) {
  const next = Array.from(list || []);
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}
